
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { getBabyDevelopmentInfo } from "@/utils/gestationUtils";
import { Clock, Calendar, Baby, ChevronRight, Crown } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

export const MobileGestationTrackingSection = () => {
  const navigatePath = "/gestation-mobile";
  
  const { data: gestationData, isLoading } = useQuery({
    queryKey: ["gestation-summary"],
    queryFn: async () => {
      try {
        const { data, error } = await supabase.auth.getUser();
        
        if (error || !data.user) {
          // User not logged in, return placeholder data
          return {
            isLoggedIn: false,
            week: 12,
            day: 3,
            dueDate: new Date(Date.now() + (40 - 12) * 7 * 24 * 60 * 60 * 1000),
          };
        }
        
        // Get actual gestation data for the logged-in user
        const { data: gestationData, error: gestationError } = await supabase
          .from("gestation_tracking")
          .select("*")
          .order("created_at", { ascending: false })
          .limit(1)
          .single();
        
        if (gestationError || !gestationData) {
          return {
            isLoggedIn: true,
            week: 12,
            day: 3,
            dueDate: new Date(Date.now() + (40 - 12) * 7 * 24 * 60 * 60 * 1000),
          };
        }
        
        // Calculate current week and day
        const startDate = new Date(gestationData.pregnancy_start_date);
        const today = new Date();
        const daysDiff = Math.floor((today.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
        const week = Math.floor(daysDiff / 7) + 1;
        const day = daysDiff % 7;
        
        // Calculate due date
        const dueDate = new Date(startDate);
        dueDate.setDate(dueDate.getDate() + 280); // ~40 weeks
        
        return {
          isLoggedIn: true,
          week,
          day,
          dueDate,
          ...gestationData,
        };
      } catch (error) {
        console.error("Error fetching gestation data:", error);
        return {
          isLoggedIn: false,
          week: 12,
          day: 3,
          dueDate: new Date(Date.now() + (40 - 12) * 7 * 24 * 60 * 60 * 1000),
        };
      }
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
  
  // Get baby development info for the current week
  const developmentInfo = gestationData ? getBabyDevelopmentInfo(gestationData.week) : null;
  
  const formattedDueDate = gestationData?.dueDate
    ? new Intl.DateTimeFormat("fr-FR", { day: "numeric", month: "long", year: "numeric" }).format(
        new Date(gestationData.dueDate)
      )
    : "";
  
  return (
    <div className="px-4 py-6 space-y-4">
      <h2 className="text-2xl font-bold">Suivi de Grossesse</h2>
      <p className="text-muted-foreground">
        Suivez chaque étape du développement de votre bébé
      </p>
      
      {isLoading ? (
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            <div className="p-5">
              <Skeleton className="h-4 w-3/4 mb-3" />
              <Skeleton className="h-4 w-1/2" />
              <div className="flex gap-3 mt-4">
                <Skeleton className="h-12 w-12 rounded-full" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-3 w-28" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="overflow-hidden bg-gradient-to-br from-pink-50 to-pink-100 border-none shadow-md">
          <CardContent className="p-0">
            <div className="p-5">
              <div className="flex justify-between items-start mb-3">
                <div>
                  <span className="text-pink-900 text-sm font-medium">Semaine</span>
                  <h3 className="text-3xl font-bold text-pink-700">{gestationData?.week}</h3>
                  <span className="text-pink-600 text-sm">
                    Jour {gestationData?.day}
                  </span>
                </div>
                <div className="bg-pink-200/70 text-pink-800 p-2 rounded-full">
                  <Crown className="h-6 w-6" />
                </div>
              </div>
              
              <div className="flex gap-3 my-4 animate-[fadeIn_0.5s]">
                <div className="bg-white/60 rounded-full p-3 shrink-0">
                  <Baby className="h-6 w-6 text-pink-600" />
                </div>
                <div>
                  <p className="font-medium text-pink-900">{developmentInfo?.title}</p>
                  <p className="text-sm text-pink-700 line-clamp-2">{developmentInfo?.description}</p>
                </div>
              </div>
              
              <div className="flex items-center gap-2 mt-4 text-sm text-pink-800">
                <Calendar className="h-4 w-4" />
                <span>Terme prévu: {formattedDueDate}</span>
              </div>
            </div>
            
            <div className="bg-pink-100/80 p-3 flex justify-between items-center border-t border-pink-200/50">
              <div className="flex items-center gap-2 text-sm text-pink-800">
                <Clock className="h-4 w-4" />
                <span>Mis à jour aujourd'hui</span>
              </div>
              <Button
                asChild
                variant="ghost"
                size="sm"
                className="text-pink-800 hover:text-pink-900 hover:bg-pink-200/50"
              >
                <Link to={navigatePath}>
                  Détails <ChevronRight className="h-3.5 w-3.5 ml-1" />
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Some key features */}
      <div className="grid grid-cols-2 gap-3">
        <Button 
          asChild
          variant="outline"
          className="justify-start h-auto py-3 font-normal bg-white hover:bg-pink-50"
        >
          <Link to={navigatePath}>
            <Calendar className="h-4 w-4 mr-2 text-pink-600" />
            <div className="text-left">
              <div className="font-medium">Calendrier</div>
              <span className="text-xs text-muted-foreground">Étapes clés</span>
            </div>
          </Link>
        </Button>
        
        <Button 
          asChild
          variant="outline"
          className="justify-start h-auto py-3 font-normal bg-white hover:bg-pink-50"
        >
          <Link to={navigatePath}>
            <Baby className="h-4 w-4 mr-2 text-pink-600" />
            <div className="text-left">
              <div className="font-medium">Développement</div>
              <span className="text-xs text-muted-foreground">Suivi détaillé</span>
            </div>
          </Link>
        </Button>
      </div>
    </div>
  );
};
